import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { MapPin, TrendingUp, Users, Radio, Calendar, ArrowRight, CheckCircle2 } from 'lucide-react';

interface MarketingRecommendation {
  id: string;
  title: string;
  description: string;
  cost: string;
  reach: string;
  impact: 'low' | 'medium' | 'high';
  timeRequired: string;
  aiGenerated: boolean;
  tags: string[];
}

const getRegionName = (regionSlug: string): string => {
  const regions: Record<string, string> = {
    "north america": "North America",
    "europe": "Europe",
    "asia": "Asia",
    "oceania": "Oceania",
    "south america": "South America",
    "africa": "Africa"
  };
  return regions[regionSlug] || regionSlug;
};

const getMarketingRecommendations = (region: string, careerStatus: string): MarketingRecommendation[] => {
  const emergingRecommendations: MarketingRecommendation[] = [
    {
      id: "rec1",
      title: "Local Radio Station Campaign",
      description: "Partner with top local radio stations for interviews and song features to build regional presence.",
      cost: "$500-1,500",
      reach: "10,000-50,000 listeners",
      impact: "medium",
      timeRequired: "2-4 weeks",
      aiGenerated: true,
      tags: ["radio", "interviews", "regional"]
    },
    {
      id: "rec2",
      title: "Regional Influencer Partnerships",
      description: "Collaborate with micro-influencers in the region for authentic promotion to targeted audiences.",
      cost: "$300-1,000",
      reach: "15,000-60,000 followers",
      impact: "medium",
      timeRequired: "1-3 weeks",
      aiGenerated: true,
      tags: ["social media", "influencers", "authentic"]
    },
    {
      id: "rec3",
      title: "Local Venue Tour",
      description: "Book shows at popular small to mid-sized venues across the region to build a live audience base.",
      cost: "$1,000-3,000",
      reach: "500-2,000 attendees",
      impact: "high",
      timeRequired: "1-2 months",
      aiGenerated: false,
      tags: ["live shows", "touring", "fanbase building"]
    }
  ];
  
  const establishedRecommendations: MarketingRecommendation[] = [
    {
      id: "rec4",
      title: "Regional PR Campaign",
      description: "Full-scale PR campaign targeting regional media outlets, newspapers, and entertainment blogs.",
      cost: "$3,000-8,000",
      reach: "100,000-500,000 impressions",
      impact: "high",
      timeRequired: "1-2 months",
      aiGenerated: true,
      tags: ["PR", "media coverage", "professional"]
    },
    {
      id: "rec5",
      title: "Regional Festival Appearances",
      description: "Secure slots at medium to large regional festivals to maximize exposure and grow your audience.",
      cost: "$5,000-15,000",
      reach: "10,000-50,000 attendees",
      impact: "high",
      timeRequired: "3-6 months planning",
      aiGenerated: false,
      tags: ["festivals", "live performance", "exposure"]
    },
    {
      id: "rec6",
      title: "Localized Streaming Ad Campaign",
      description: "Run targeted ads on Spotify, Apple Music and other streaming platforms specifically for this region.",
      cost: "$1,000-5,000",
      reach: "50,000-200,000 listeners",
      impact: "medium",
      timeRequired: "2-4 weeks",
      aiGenerated: true,
      tags: ["streaming", "digital ads", "targeting"]
    }
  ];
  
  const majorRecommendations: MarketingRecommendation[] = [
    {
      id: "rec7",
      title: "Arena Tour with Local Support Acts",
      description: "Schedule arena shows across the region with popular local artists as opening acts to strengthen regional connection.",
      cost: "$100,000-500,000",
      reach: "50,000-200,000 attendees",
      impact: "high",
      timeRequired: "6-12 months planning",
      aiGenerated: false,
      tags: ["arena tour", "live shows", "high capacity"]
    },
    {
      id: "rec8",
      title: "Regional Brand Partnership",
      description: "Partner with a major regional brand for co-branded campaign including commercials, product lines, and exclusive content.",
      cost: "$50,000-250,000",
      reach: "500,000-5,000,000 impressions",
      impact: "high",
      timeRequired: "3-6 months",
      aiGenerated: true,
      tags: ["brand deal", "sponsorship", "co-marketing"]
    },
    {
      id: "rec9",
      title: "Regional Exclusive Release Strategy",
      description: "Create region-specific exclusive content, bonus tracks or special editions to drive engagement in this territory.",
      cost: "$10,000-50,000",
      reach: "200,000-1,000,000 impressions",
      impact: "medium",
      timeRequired: "1-3 months",
      aiGenerated: true,
      tags: ["exclusive content", "special edition", "regional focus"]
    }
  ];
  
  switch(careerStatus) {
    case 'emerging':
      return emergingRecommendations;
    case 'established':
      return establishedRecommendations;
    case 'major':
      return majorRecommendations;
    default:
      return [...emergingRecommendations, ...establishedRecommendations].slice(0, 3);
  }
};

const RegionalMarketingRecommendations: React.FC = () => {
  const { region } = useParams<{ region: string }>();
  const [careerStatus, setCareerStatus] = useState<string>('established');
  const [selectedRecommendation, setSelectedRecommendation] = useState<string | null>(null);
  
  const regionSlug = region || 'north america';
  const recommendations = getMarketingRecommendations(regionSlug, careerStatus);
  const regionName = getRegionName(regionSlug);
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Marketing in {regionName}</h1>
          <p className="text-muted-foreground">Targeted recommendations based on your chart performance</p>
        </div>
        <Badge variant="outline" className="bg-background/30 flex items-center gap-1.5 px-3 py-1.5 text-sm">
          <MapPin className="h-4 w-4 text-blue-500" />
          Region: {regionName}
        </Badge>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>Adjust Recommendations</CardTitle>
          <CardDescription>Select your career stage to get relevant marketing suggestions</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue={careerStatus} onValueChange={setCareerStatus} className="w-full">
            <TabsList className="grid grid-cols-3 mb-4">
              <TabsTrigger value="emerging">Emerging Artist</TabsTrigger>
              <TabsTrigger value="established">Established Artist</TabsTrigger>
              <TabsTrigger value="major">Major Artist</TabsTrigger>
            </TabsList>
            
            <TabsContent value="emerging">
              <p className="text-sm text-muted-foreground mb-2">
                Recommendations for artists building their career with limited budget and regional recognition.
              </p>
            </TabsContent>
            <TabsContent value="established">
              <p className="text-sm text-muted-foreground mb-2">
                Recommendations for artists with moderate success, growing audience, and mid-level budget.
              </p>
            </TabsContent>
            <TabsContent value="major">
              <p className="text-sm text-muted-foreground mb-2">
                Recommendations for successful artists with large budget and significant industry recognition.
              </p>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {recommendations.map((rec) => (
          <Card 
            key={rec.id}
            className={`border ${selectedRecommendation === rec.id ? 'border-soundcharts-purple' : 'border-border'} 
                       transition-all hover:shadow-md cursor-pointer`}
            onClick={() => setSelectedRecommendation(rec.id)}
          >
            <CardHeader className="pb-2">
              <div className="flex justify-between">
                <CardTitle className="text-lg">{rec.title}</CardTitle>
                {rec.aiGenerated && (
                  <Badge variant="outline" className="bg-purple-500/10 text-purple-500 border-purple-500/20">
                    AI Generated
                  </Badge>
                )}
              </div>
              <CardDescription>{rec.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Estimated cost:</span>
                  <span className="font-medium">{rec.cost}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Expected reach:</span>
                  <span className="font-medium">{rec.reach}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Impact:</span>
                  <Badge variant="outline" className={`
                    ${rec.impact === 'low' ? 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20' : 
                      rec.impact === 'medium' ? 'bg-blue-500/10 text-blue-500 border-blue-500/20' : 
                      'bg-green-500/10 text-green-500 border-green-500/20'}
                  `}>
                    {rec.impact.charAt(0).toUpperCase() + rec.impact.slice(1)}
                  </Badge>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Time required:</span>
                  <span className="font-medium">{rec.timeRequired}</span>
                </div>
                
                <div className="flex flex-wrap gap-1.5 pt-2">
                  {rec.tags.map((tag, index) => (
                    <Badge key={index} variant="outline" className="bg-background/30">{tag}</Badge>
                  ))}
                </div>
                
                <Button 
                  className="w-full mt-2" 
                  variant={selectedRecommendation === rec.id ? "default" : "outline"}
                >
                  {selectedRecommendation === rec.id ? (
                    <span className="flex items-center gap-1.5">
                      <CheckCircle2 className="h-4 w-4" />
                      Selected
                    </span>
                  ) : (
                    <span>Select</span>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      {selectedRecommendation && (
        <div className="flex justify-end">
          <Button className="gap-2">
            <span>Create Campaign</span>
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default RegionalMarketingRecommendations;
